import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BusinessRole } from "fe-shared/src/types/domain";

import { LinkedTransaction, TransactionStatus, TransactionType } from "../../types";
import { formatTransactionNumber } from "../../helpers/format";
import { LinkedIcon, SubdirectoryArrow } from "../Icons";

import styles from "./LinkedTransactions.module.scss";

interface Props {
  transactionType: TransactionType;
  linkedTransactions: LinkedTransaction[];
  isStaffView?: boolean;
  target?: string;
  targetBusinessRoleCode?: BusinessRole;
  requestNumber?: string | null;
  canOpenRequest?: boolean;
}

const childTypes = [TransactionType.Receipt, TransactionType.Pickup];

const adjustmentsTypes = [TransactionType.Adjustment];

const LinkedTransactions = ({
  transactionType,
  linkedTransactions,
  target,
  isStaffView = false,
  targetBusinessRoleCode,
  requestNumber,
  canOpenRequest = false,
}: Props) => {
  const { t } = useTranslation();

  const isChildType = childTypes.includes(transactionType);

  const isLinkedIcon =
    adjustmentsTypes.includes(transactionType) ||
    (!isStaffView && linkedTransactions.length);

  const isDisposition = transactionType === TransactionType.Disposition;

  return (
    <div className={styles.linkedBlock}>
      {isLinkedIcon ? (
        <LinkedIcon className={styles.linkedIcon} width={12} height={16} />
      ) : (
        <SubdirectoryArrow
          className={styles.linkedIcon}
          width={12}
          height={12}
          isUp={isChildType}
        />
      )}
      {isDisposition ? (
        <span className={styles.destination}>
          {t(`businessRoles.${targetBusinessRoleCode}`)}
        </span>
      ) : (
        <div className={styles.transactions}>
          {linkedTransactions.map((item) => {
            if (
              item.transactionNumber === "Waiting Receipt"
            ) {
              return (
                <span
                  key={item.transactionNumber}
                  className={styles.linkedTransactionDisabled}
                >
                  {t("transactions.waitingReceipt")}
                </span>
              );
            }

            if (
              !isStaffView &&
              item.transactionType !== TransactionType.Adjustment
            ) {
              return (
                <span
                  key={item.transactionNumber}
                  className={styles.linkedTransactionDisabled}
                >
                  {`${t(
                    `transactions.type.${item.transactionType}`
                  )} ${formatTransactionNumber(item.transactionNumber)}`}
                </span>
              );
            }

            return (
              <Link
                target={target}
                key={item.transactionNumber}
                className={styles.linkedTransaction}
                to={`/transactions/${item.transactionNumber}`}
              >
                {`${t(
                  `transactions.type.${item.transactionType}`
                )} ${formatTransactionNumber(item.transactionNumber)}`}
              </Link>
            );
          })}
          {requestNumber &&
            (canOpenRequest ? (
              <Link
                key={requestNumber}
                className={styles.linkedTransaction}
                to={`/requests/${requestNumber}`}
              >
                {`${t(
                  "transactions.linkedRequest"
                )} ${formatTransactionNumber(requestNumber)}`}
              </Link>
            ) : (
              <span
                key={requestNumber}
                className={styles.linkedTransactionDisabled}
              >
                {`${t(
                  "transactions.linkedRequest"
                )} ${formatTransactionNumber(requestNumber)}`}
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

export default LinkedTransactions;
